// src/hooks/useNotes.js - Hook for accessing customer notes
import useNoteStore from '../stores/NoteStore';
import { useSocket } from '../contexts/SocketContext';

/**
 * Custom hook for accessing customer notes from NoteStore
 *
 * Uses Zustand NoteStore for state management.
 * Store initialization is handled at app level in SocketContext.
 *
 * @returns {object} Note data and operations
 */
export const useNotes = () => {
  const socket = useSocket();

  // Notes by customer
  const notesByCustomer = useNoteStore(state => state.notesByCustomer);

  // Selected note (for editing)
  const selectedNote = useNoteStore(state => state.selectedNote);
  const selectedNoteId = useNoteStore(state => state.selectedNoteId);

  // Granular saving flags
  const creatingNote = useNoteStore(state => state.creatingNote);
  const savingNote = useNoteStore(state => state.savingNote);
  const deletingNote = useNoteStore(state => state.deletingNote);

  // Operations
  const loadCustomerNotes = useNoteStore(state => state.loadCustomerNotes);
  const createNote = useNoteStore(state => state.createNote);
  const updateNote = useNoteStore(state => state.updateNote);
  const deleteNote = useNoteStore(state => state.deleteNote);
  const pinNote = useNoteStore(state => state.pinNote);

  // Selection
  const selectNote = useNoteStore(state => state.selectNote);
  const clearSelection = useNoteStore(state => state.clearSelection);
  const clearSelectedNote = useNoteStore(state => state.clearSelectedNote);
  const startNewNote = useNoteStore(state => state.startNewNote);

  // Utility functions
  const getNotesForCustomer = useNoteStore(state => state.getNotesForCustomer);
  const isLoadingNotesForCustomer = useNoteStore(state => state.isLoadingNotesForCustomer);
  const getTotalNotesForCustomer = useNoteStore(state => state.getTotalNotesForCustomer);
  const clearNotesForCustomer = useNoteStore(state => state.clearNotesForCustomer);

  // Connection state
  const isConnected = useNoteStore(state => state.isConnected);

  // Internal initialization state
  const _initialized = useNoteStore(state => state._initialized);

  // Fallback initialization check
  if (socket.isAuthenticated && !socket.storesInitialized && !_initialized) {
    console.warn('useNotes: Store not initialized at app level, trying fallback');
    socket.initializeStore('NoteStore');
  }

  return {
    // Data
    notesByCustomer,
    selectedNote,
    selectedNoteId,

    // Granular saving flags
    creatingNote,
    savingNote,
    deletingNote,

    // Operations
    loadCustomerNotes,
    createNote,
    updateNote,
    deleteNote,
    pinNote,

    // Selection
    selectNote,
    clearSelection,
    clearSelectedNote,
    startNewNote,

    // Utility functions
    getNotesForCustomer,
    isLoadingNotesForCustomer,
    getTotalNotesForCustomer,
    clearNotesForCustomer,

    // Connection state
    isConnected: socket.isAuthenticated
  };
};

export default useNotes;
